import React from 'react';
import * as Form from 'react-hook-form';
import { Keyboard, Platform } from 'react-native';
import DeviceInfo from 'react-native-device-info';
import { t } from '@lingui/macro';
import { AxiosError } from 'axios';

import SignIn from '@src/api/SignIn';
import {
  passwordRegex,
  phoneNumberExtractor,
  phoneNumberValidator,
  phoneOrUsernameRegex,
  usernameValidator,
} from '@src/helpers';
import useAuthenticationTokens from '@src/hooks/useAuthenticationTokens';
import useAxiosErrorHandler from '@src/hooks/useAxiosErrorHandler';
import useAxiosRequestConfig from '@src/hooks/useAxiosRequestConfig';
import useSnackBar from '@src/hooks/useSnackBar';
import { i18n, i18yup } from '@src/i18n';

import { LoginFormData } from './types';

const useLoginForm = () => {
  const snackBar = useSnackBar();
  const { setTokens } = useAuthenticationTokens();
  const axiosRequestConfig = useAxiosRequestConfig();
  const axiosErrorHandler = useAxiosErrorHandler();
  const [loading, setLoading] = React.useState(false);
  const form = Form.useForm<LoginFormData>({
    validationSchema: i18yup.object<LoginFormData>({
      region: i18yup
        .string()
        .required()
        .default('IR')
        .label(i18n._(t`Region`)),
      identity: i18yup
        .string()
        .required()
        .matches(
          phoneOrUsernameRegex,
          i18n._(t`Must be a valid phone number or username`)
        )
        .label(i18n._(t`Phone or Username`)),
      password: i18yup
        .string()
        .required()
        .matches(
          passwordRegex,
          i18n._(t`Password must be at least 6 characters`)
        )
        .label(i18n._(t`Password`)),
    }),
  });

  const handleSubmit = React.useCallback(
    async ({ region, identity, password }: LoginFormData) => {
      setLoading(true);

      const isPhone = phoneNumberValidator(identity);
      const isUsername = usernameValidator(identity);

      try {
        Keyboard.dismiss();
        type SignInData = Parameters<typeof SignIn>[0];

        let requestData: SignInData = {
          device: DeviceInfo.getDeviceId(),
          deviceId: DeviceInfo.getUniqueId(),
          os: Platform.OS,
          identity: '',
          password,
        };

        if (isPhone) {
          requestData = {
            ...requestData,
            identity: phoneNumberExtractor(identity, region),
            region,
          };
        } else if (isUsername) {
          requestData = {
            ...requestData,
            identity,
          };
        } else {
          snackBar.show({ content: i18n._(t`Invalid Phone/Username`) });
          return;
        }

        const response = await SignIn(requestData, axiosRequestConfig);

        const accessToken = response.headers['x-auth-token'];
        if (accessToken) {
          setTokens({
            refreshToken: response.data.refreshToken,
            accessToken,
            userID: response.data.userId,
            region: response.data.region,
            setupComplete: response.data.setupComplete,
          });
        }
      } catch (err) {
        console.log(err);
        axiosErrorHandler(err);

        const status = (err as AxiosError)?.response?.status;

        if (status && status >= 400 && status < 500) {
          snackBar.show({
            content: i18n._(t`Wrong phone/username and password combination`),
          });
        } else {
          snackBar.show({ content: i18n._(t`Server error`) });
        }
      } finally {
        setLoading(false);
      }
    },
    [axiosErrorHandler, axiosRequestConfig, setTokens, snackBar]
  );

  return React.useMemo(
    () => ({
      form,
      handleSubmit: form.handleSubmit(handleSubmit),
      loading,
    }),
    [form, handleSubmit, loading]
  );
};

export default useLoginForm;
